import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import Iconify from '../../../components/iconify';
import { ASSET_STATUS_OPTIONS, ASSET_CONDITION_OPTIONS } from '../constants/assetConstants';

const UNASSIGNED = 'unassigned';

const INITIAL_VALUES = {
  name: '',
  type: '',
  status: '',
  condition: '',
  assignedTo: '',
  serialNumber: '',
  assetTag: '',
  category: '',
  location: '',
  vendor: '',
  invoiceNumber: '',
  purchaseDate: '',
  warrantyExpiry: '',
  purchasePrice: '',
  assignmentNotes: '',
};

// Build the payload the backend expects: send only the fields the user
// actually filled in, and translate the "unassigned" sentinel to null.
const buildPayload = (values) => {
  const payload = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value === '' || value === undefined || value === null) return;
    payload[key] = value;
  });

  if (values.assignedTo === UNASSIGNED) {
    payload.assignedTo = null;
  } else if (values.assignedTo) {
    payload.assignedTo = Number(values.assignedTo);
  }

  if (values.purchasePrice !== '') {
    payload.purchasePrice = Number(values.purchasePrice);
  }

  return payload;
};

export default function AssetFormModal({ open, onClose, onSubmit, selectedRow, action, users }) {
  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      type: Yup.string().required('Type is required'),
      status: Yup.string().oneOf(
        ASSET_STATUS_OPTIONS.map((option) => option.value),
        'Invalid status'
      ),
      condition: Yup.string().oneOf(
        ASSET_CONDITION_OPTIONS.map((option) => option.value),
        'Invalid condition'
      ),
      purchasePrice: Yup.number().typeError('Must be a number').min(0, 'Must be positive').nullable(),
    }),
    onSubmit: async (values) => {
      try {
        await onSubmit(buildPayload(values));
        onClose();
      } catch (error) {
        toast.error(error?.response?.data?.message ?? error.message);
      }
    },
    enableReinitialize: true, // Important for updating values in edit mode
  });

  useEffect(() => {
    if (action === 'edit' && selectedRow?.original) {
      const asset = selectedRow.original;
      const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');
      formik.setValues({
        name: asset.name || '',
        type: asset.type || '',
        status: asset.status || '',
        condition: asset.condition || '',
        assignedTo: asset.assignedTo ? asset.assignedTo.toString() : '',
        serialNumber: asset.serialNumber || '',
        assetTag: asset.assetTag || '',
        category: asset.category || '',
        location: asset.location || '',
        vendor: asset.vendor || '',
        invoiceNumber: asset.invoiceNumber || '',
        purchaseDate: dateOnly(asset.purchaseDate),
        warrantyExpiry: dateOnly(asset.warrantyExpiry),
        purchasePrice: asset.purchasePrice != null ? asset.purchasePrice : '',
        assignmentNotes: '',
      });
    } else {
      formik.resetForm();
    }
  }, [selectedRow, action, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const textField = (name, label, extra = {}) => (
    <TextField
      margin="dense"
      label={label}
      name={name}
      fullWidth
      variant="outlined"
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
      {...extra}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle sx={{ pr: 6 }}>
          {action === 'edit' ? 'Edit Asset' : 'Create Asset'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              {textField('name', 'Asset Name', { autoFocus: true })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('type', 'Asset Type')}
            </Grid>

            <Grid item xs={12} sm={6}>
              {textField('status', 'Status', {
                select: true,
                helperText:
                  (formik.touched.status && formik.errors.status) ||
                  'Optional — auto-set from assignment if left blank',
                children: [
                  <MenuItem key="" value="">
                    <em>Auto</em>
                  </MenuItem>,
                  ...ASSET_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )),
                ],
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('condition', 'Condition', {
                select: true,
                children: [
                  <MenuItem key="" value="">
                    <em>None</em>
                  </MenuItem>,
                  ...ASSET_CONDITION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )),
                ],
              })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {textField('assignedTo', 'Assigned To', {
                select: true,
                children: [
                  <MenuItem key={UNASSIGNED} value={UNASSIGNED}>
                    <em>Unassigned</em>
                  </MenuItem>,
                  ...(users?.map((user) => (
                    <MenuItem key={user.id} value={user.id.toString()}>
                      {user.userName}
                    </MenuItem>
                  )) || []),
                ],
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('category', 'Category')}
            </Grid>

            <Grid item xs={12} sm={6}>
              {textField('serialNumber', 'Serial Number')}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('assetTag', 'Asset Tag')}
            </Grid>

            <Grid item xs={12} sm={6}>
              {textField('location', 'Location')}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('vendor', 'Vendor')}
            </Grid>

            <Grid item xs={12} sm={6}>
              {textField('invoiceNumber', 'Invoice Number')}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('purchasePrice', 'Purchase Price', { type: 'number' })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {textField('purchaseDate', 'Purchase Date', {
                type: 'date',
                InputLabelProps: { shrink: true },
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField('warrantyExpiry', 'Warranty Expiry', {
                type: 'date',
                InputLabelProps: { shrink: true },
              })}
            </Grid>

            <Grid item xs={12}>
              {textField('assignmentNotes', 'Assignment Notes', {
                multiline: true,
                minRows: 2,
                helperText: 'Optional — stored on the history record when (re)assigning',
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={formik.isSubmitting}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={formik.isSubmitting}>
            {action === 'edit' ? 'Update' : 'Create'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

AssetFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  selectedRow: PropTypes.object,
  action: PropTypes.string,
  users: PropTypes.array.isRequired,
};
